import { cacheInvalidate } from './apiCache';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

// --- Types ---

export interface JurisdictionEntry {
  country: string;
  state?: string | null;
}

export interface ContactEntry {
  id?: number;
  role: string;
  name: string;
  email: string;
  title?: string | null;
}

export interface CompanyProfile {
  id: number;
  user_id: number;
  company_name: string;
  industry: string | null;
  company_size: string | null;
  headquarters_location: string | null;
  operating_jurisdictions: JurisdictionEntry[] | null;
  business_model: string[] | null;
  ai_usage_declaration: string[] | null;
  processes_personal_data: boolean | null;
  regulatory_declarations: string[] | null;
  contacts: ContactEntry[];
  completion_status: string;
  created_at: string;
  updated_at: string;
}

export interface CompanyProfilePayload {
  company_name: string;
  industry?: string;
  company_size?: string;
  headquarters_location?: string;
  operating_jurisdictions?: JurisdictionEntry[];
  business_model?: string[];
  ai_usage_declaration?: string[];
  processes_personal_data?: boolean;
  regulatory_declarations?: string[];
  contacts?: Omit<ContactEntry, 'id'>[];
  completion_status?: string;
}

// --- API Functions ---

export async function fetchCompanyProfile(): Promise<CompanyProfile> {
  const res = await fetch(`${API_BASE_URL}/api/v1/company-profile`, {
    headers: authHeaders(),
  });
  return parseResponse<CompanyProfile>(res);
}

function invalidateCompanyProfileCaches(): void {
  cacheInvalidate('company-profile');
  cacheInvalidate('enterprise-profile');
  cacheInvalidate('repo-summary');
}

export async function saveCompanyProfile(payload: CompanyProfilePayload): Promise<CompanyProfile> {
  const res = await fetch(`${API_BASE_URL}/api/v1/company-profile`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponse<CompanyProfile>(res);
  invalidateCompanyProfileCaches();
  return data;
}

export async function updateCompanyProfile(payload: Partial<CompanyProfilePayload>): Promise<CompanyProfile> {
  const res = await fetch(`${API_BASE_URL}/api/v1/company-profile`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(payload),
  });
  const data = await parseResponse<CompanyProfile>(res);
  invalidateCompanyProfileCaches();
  return data;
}

export async function suggestRegulations(jurisdictions: JurisdictionEntry[]): Promise<string[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/company-profile/suggest-regulations`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(jurisdictions),
  });
  return parseResponse<string[]>(res);
}
